// src/components/league/LeaguePlayerPool.tsx
// REVISED: This component now implements the same best-in-class responsive split layout as PlayerPool.tsx.
// It features a compact, horizontally scrollable table with sticky columns for mobile,
// while preserving the full-featured layout for desktop.

import React, { FC } from 'react';
import { PlayerRead } from '@/api/schemas';
import { formatPlayerDisplayName, formatWaiverProcessDate } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';

interface LeaguePlayerPoolProps {
    players: PlayerRead[];
    playerFetchError: string | null;
    searchTerm: string;
    onSearchTermChange: (term: string) => void;
    positionFilter: LeaguePositionFilter;
    onPositionFilterChange: (filter: LeaguePositionFilter) => void;
    sortConfig: { sortBy: string; sortOrder: 'asc' | 'desc' };
    onSortChange: (newSortBy: string, newSortOrder: 'asc' | 'desc') => void;
    onPlayerRowClick?: (player: PlayerRead) => void;
    mode?: 'league' | 'public';
    isLmPreDraftMode?: boolean;
    onAddPlayerToRoster?: (player: PlayerRead, actionType?: 'WAIVER' | 'FCFS') => void;
    onTradePlayer?: (player: PlayerRead) => void;
    availabilityFilter?: 'available' | 'rostered' | 'all';
    onAvailabilityFilterChange?: (filter: 'available' | 'rostered' | 'all') => void;
    currentRosterPlayerIds?: Set<number>;
}

export type LeaguePositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

const LeaguePlayerPool: FC<LeaguePlayerPoolProps> = ({
    players, playerFetchError, searchTerm, onSearchTermChange,
    positionFilter, onPositionFilterChange, sortConfig, onSortChange, onPlayerRowClick,
    mode = 'league', isLmPreDraftMode = false, onAddPlayerToRoster = () => {},
    onTradePlayer = () => {}, availabilityFilter = 'all', onAvailabilityFilterChange = () => {},
    currentRosterPlayerIds = new Set(),
}) => {
    // --- All filter and sort logic remains the same ---
    const handleSortSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const [key, direction] = event.target.value.split('-') as [string, 'asc' | 'desc'];
        onSortChange(key, direction);
    };
    const currentSortValue = `${sortConfig.sortBy}-${sortConfig.sortOrder}`;
    const sortOptions: { value: string; label: string }[] = [ { value: 'fantasy_points-desc', label: 'Total Points (High-Low)' }, { value: 'fppg-desc', label: 'FPPG (High-Low)' }, { value: 'last_week_points-desc', label: 'Last Week (High-Low)' }, { value: 'name-asc', label: 'Name (A-Z)' }, { value: 'primary_position-asc', label: 'Position' }, { value: 'goals-desc', label: 'Goals (High-Low)' }, { value: 'shots-desc', label: 'Shots (High-Low)' }, ];
    const positionButtons: { label: string; value: LeaguePositionFilter }[] = [ { label: 'All', value: 'ALL' }, { label: 'FW', value: 'FW' }, { label: 'MF', value: 'MF' }, { label: 'DF', value: 'DF' }, { label: 'GK', value: 'GK' }, ];
    const availabilityButtons: { label: string; value: 'available' | 'rostered' | 'all' }[] = [ { label: 'Available', value: 'available' }, { label: 'Rostered', value: 'rostered' }, { label: 'All Players', value: 'all' }, ];
    const formatStat = (value: number | null | undefined): string => { if (value === null || value === undefined || isNaN(value)) return '-'; return value.toFixed(1); };
    // --- End of unchanged logic ---

    // Shared logic for rendering a single player row
    const renderPlayerRowLogic = (player: PlayerRead) => {
        const isRosteredByCurrentUser = currentRosterPlayerIds.has(player.id);
        const displayName = formatPlayerDisplayName(player);
        const teamAbbr = getAbbreviation(player.current_team_name);
        const playerPositionDisplay = player.primary_position || 'N/A';
        const availabilityStatus = player.current_league_availability_status;
        let btnText = "N/A";
        let btnStyle = "bg-gray-700 text-gray-500 cursor-not-allowed";
        let btnTitle = `Player status: ${availabilityStatus || 'Unknown'}`;
        let btnDisabled = true;
        let actionType: 'WAIVER' | 'FCFS' | 'TRADE' | null = null;

        if (isLmPreDraftMode) {
            btnText = "+ Add";
            btnStyle = "bg-green-600 hover:bg-green-500 text-white";
            btnTitle = "Add player to selected team's roster";
            btnDisabled = currentRosterPlayerIds.has(player.id);
            if (btnDisabled) {
                btnText = "On Roster";
                btnStyle = "bg-blue-800/60 text-blue-300 cursor-default";
            }
        } else if (isRosteredByCurrentUser) {
            btnText = "On Roster";
            btnStyle = "bg-blue-800/60 text-blue-300 cursor-default";
            btnTitle = "Player is on your team";
        } else if (player.owner_team_id != null) {
            btnText = "Trade";
            btnStyle = "bg-blue-600 hover:bg-blue-500 text-white";
            btnTitle = `Propose trade with ${player.owner_team_name || 'this team'}`;
            btnDisabled = false;
            actionType = 'TRADE';
        } else if (availabilityStatus === "LOCKED") {
            // Legacy state: unrostered players are now always WAIVER or FCFS.
            // Kept only so stale cached payloads render harmlessly.
            btnText = "Locked";
            btnStyle = "bg-gray-600 text-gray-400 cursor-not-allowed";
            btnTitle = "Player's match has started or is over";
        } else if (availabilityStatus === "WAIVER") {
            btnText = "Claim";
            btnStyle = "bg-yellow-600 hover:bg-yellow-500 text-white";
            btnTitle = player.waiver_clears_at
                ? `On waivers - claims process ${formatWaiverProcessDate(player.waiver_clears_at)}`
                : "Submit waiver claim for this player";
            btnDisabled = false;
            actionType = 'WAIVER';
        } else if (availabilityStatus === "FCFS") {
            btnText = "+ Add";
            btnStyle = "bg-green-600 hover:bg-green-500 text-white";
            btnTitle = "Add as Free Agent";
            btnDisabled = false;
            actionType = 'FCFS';
        }

        return {
            isRosteredByCurrentUser, displayName, teamAbbr, playerPositionDisplay,
            availabilityStatus, btnText, btnStyle, btnTitle, btnDisabled, actionType
        };
    };

    return (
        <div className={`bg-primary p-4 rounded-lg shadow-lg flex flex-col text-gray-200 font-body flex-grow min-h-0`}>
            {/* Filter Controls (Unchanged) */}
            <div className="mb-2">
                <input type="text" placeholder="Search Players..." value={searchTerm} onChange={(e) => onSearchTermChange(e.target.value)} className="form-input" />
            </div>
            <div className="mb-3 flex flex-col sm:flex-row gap-2 justify-between">
                <div className="flex-shrink-0 flex items-center space-x-1 bg-primary-dark p-1 rounded-md border border-gray-600">
                    {positionButtons.map((btn) => (<button key={btn.value} onClick={() => onPositionFilterChange(btn.value)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${positionFilter === btn.value ? 'bg-accent-light-blue text-primary' : 'text-gray-300 hover:bg-primary/70'}`}>{btn.label}</button>))}
                </div>
                {mode === 'league' && (
                    <div className="flex-shrink-0 flex items-center space-x-1 bg-primary-dark p-1 rounded-md border border-gray-600">
                        {availabilityButtons.map((btn) => (<button key={btn.value} onClick={() => onAvailabilityFilterChange(btn.value)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${availabilityFilter === btn.value ? 'bg-accent-yellow text-primary' : 'text-gray-300 hover:bg-primary/70'}`}>{btn.label}</button>))}
                    </div>
                )}
            </div>
            <div className="mb-3 flex items-center space-x-2">
                <label htmlFor="sort-select-league-pool" className="text-xs font-semibold uppercase text-accent-yellow flex-shrink-0">SORT BY</label>
                <select id="sort-select-league-pool" value={currentSortValue} onChange={handleSortSelectChange} className="form-select flex-grow">
                    {sortOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
            </div>
            
            <div className={`overflow-y-auto flex-grow border-t border-gray-700 scrollbar-mobile-hide`}>
                {/* --- RESPONSIVE SPLIT --- */}
                {/* 1. Mobile View: Horizontally Scrolling Table */}
                <div className="sm:hidden overflow-x-auto">
                    <table className="w-full text-left text-sm min-w-[550px]">
                        <thead className="sticky top-0 bg-primary z-10">
                            <tr>
                                <th className="sticky left-0 bg-primary z-20 p-2 text-xs font-semibold text-gray-400 uppercase">Player</th>
                                <th className="p-2 text-xs font-semibold text-gray-400 uppercase text-center w-12">Pos</th>
                                <th className="p-2 text-xs font-semibold text-gray-400 uppercase text-right">Total</th>
                                <th className="p-2 text-xs font-semibold text-gray-400 uppercase text-right">Last Wk</th>
                                <th className="p-2 text-xs font-semibold text-gray-400 uppercase text-right">FPPG</th>
                                <th className="sticky right-0 bg-primary z-20 p-2 text-xs font-semibold text-gray-400 uppercase text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {players.map((player) => {
                                const logic = renderPlayerRowLogic(player);
                                const rowBgClass = logic.isRosteredByCurrentUser ? 'bg-blue-900/20' : 'hover:bg-primary/60';
                                const playerGradientBg = logic.isRosteredByCurrentUser ? 'after:to-blue-900/20' : 'after:to-primary';
                                const actionGradientBg = logic.isRosteredByCurrentUser ? 'before:to-blue-900/20' : 'before:to-primary';
                                
                                return (
                                    <tr key={player.id} className={`${rowBgClass} transition-colors`}>
                                        <td onClick={() => onPlayerRowClick && onPlayerRowClick(player)} className={`sticky left-0 z-10 p-2 cursor-pointer ${logic.isRosteredByCurrentUser ? 'bg-blue-900/20' : 'bg-primary'} relative after:absolute after:top-0 after:right-0 after:w-4 after:h-full after:bg-gradient-to-r after:from-transparent ${playerGradientBg} after:pointer-events-none`}>
                                            <p className="font-medium truncate text-gray-100 text-xs" title={logic.displayName}>{logic.displayName}</p>
                                            <p className="text-badge text-gray-400">{logic.teamAbbr}</p>
                                        </td>
                                        <td className="p-2 text-center text-xs font-medium w-12">{logic.playerPositionDisplay}</td>
                                        <td className="p-2 text-right font-semibold text-xs">{formatStat(player.fantasy_points)}</td>
                                        <td className="p-2 text-right font-semibold text-xs">{formatStat(player.last_week_points)}</td>
                                        <td className="p-2 text-right font-semibold text-xs">{formatStat(player.fppg)}</td>
                                        <td className={`sticky right-0 z-10 p-2 text-center ${logic.isRosteredByCurrentUser ? 'bg-blue-900/20' : 'bg-primary'} relative before:absolute before:top-0 before:left-0 before:w-4 before:h-full before:bg-gradient-to-l before:from-transparent ${actionGradientBg} before:pointer-events-none`}>
                                            <button onClick={(e) => { e.stopPropagation(); if (!logic.btnDisabled && (logic.actionType === 'WAIVER' || logic.actionType === 'FCFS')) { onAddPlayerToRoster(player, logic.actionType); } else if (!logic.btnDisabled && logic.actionType === 'TRADE') { onTradePlayer(player); } else if (isLmPreDraftMode && !logic.btnDisabled) { onAddPlayerToRoster(player); } }} disabled={logic.btnDisabled} className={`w-full px-1.5 py-1 rounded text-xs font-bold transition-colors ${logic.btnStyle}`} title={logic.btnTitle}>
                                                {logic.btnText}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* 2. Desktop View: Original flexbox layout */}
                <div className="hidden sm:block">
                    <div className="sticky top-0 bg-primary z-10 flex items-center px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-700">
                        <div className={`${mode === 'public' ? 'w-[55%]' : 'w-[40%]'}`}>Player (Team)</div>
                        <div className="w-[10%] text-center">Pos</div>
                        {mode === 'league' && !isLmPreDraftMode && <div className="w-[15%]">Status</div>}
                        <div className="w-[12%] text-right" title="Total Season Fantasy Points">Total</div>
                        <div className="w-[12%] text-right" title="Last Week's Fantasy Points">Last Wk</div>
                        <div className="w-[12%] text-right" title="Fantasy Points Per Game">FPPG</div>
                        {mode === 'league' && <div className="w-[14%] text-center">Action</div>}
                    </div>
                    <div className="divide-y divide-gray-700">
                        {players.map((player) => {
                            const logic = renderPlayerRowLogic(player);
                            return (
                                <div key={player.id} className={`flex items-center justify-between p-2 text-sm hover:bg-primary-dark/80 active:bg-primary-dark/80 ${onPlayerRowClick ? 'cursor-pointer' : ''}`} onClick={() => onPlayerRowClick && onPlayerRowClick(player)}>
                                    <div className={`${mode === 'public' ? 'w-[55%]' : 'w-[40%]'} flex items-center min-w-0`}>
                                        <span className="font-medium truncate text-gray-100" title={logic.displayName}>{logic.displayName}</span>
                                        <span className="ml-1.5 text-caption font-semibold text-gray-400 hover:text-accent-yellow" title={player.current_team_name || ''}>({logic.teamAbbr})</span>
                                    </div>
                                    <div className="w-[10%] text-center font-bold text-accent-light-blue/80">{logic.playerPositionDisplay}</div>
                                    {mode === 'league' && !isLmPreDraftMode && (
                                        <div className="w-[15%] text-left text-caption text-gray-400 truncate pl-2">
                                            {player.owner_team_id != null ? (logic.isRosteredByCurrentUser ? <span className="font-bold text-green-400">MY TEAM</span> : <span>{player.owner_team_name || 'Rostered'}</span>) : (<span className="text-gray-500">Free Agent</span>)}
                                        </div>
                                    )}
                                    <div className="w-[12%] text-right font-semibold">{formatStat(player.fantasy_points)}</div>
                                    <div className="w-[12%] text-right font-semibold">{formatStat(player.last_week_points)}</div>
                                    <div className="w-[12%] text-right font-semibold">{formatStat(player.fppg)}</div>
                                    {mode === 'league' && (
                                        <div className="w-[14%] text-center">
                                            <button onClick={(e) => { e.stopPropagation(); if (!logic.btnDisabled && (logic.actionType === 'WAIVER' || logic.actionType === 'FCFS')) { onAddPlayerToRoster(player, logic.actionType); } else if (!logic.btnDisabled && logic.actionType === 'TRADE') { onTradePlayer(player); } else if (isLmPreDraftMode && !logic.btnDisabled) { onAddPlayerToRoster(player); } }} disabled={logic.btnDisabled} className={`w-full px-1.5 py-1 rounded text-xs font-bold transition-colors ${logic.btnStyle}`} title={logic.btnTitle}>
                                                {logic.btnText}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                 {players.length === 0 && (
                     <div className="text-center p-4 text-gray-500 italic text-sm">
                         {playerFetchError ? `Error: ${playerFetchError}` : 'No players match current filters.'}
                     </div>
                )}
            </div>
        </div>
    );
};

export default LeaguePlayerPool;
